import axios from '@/lib/axios'
import { useInfiniteQuery } from '@tanstack/vue-query'
import { computed, ref } from 'vue'

import { PaginatedResponse } from '@/interfaces/paginatedResponse'

import {
	RecentHotelSearch,
	RecentHotelSearchFilters,
	RecentHotelSearchVisibility,
} from '../interfaces/recentHotelSearch'

interface RecentHotelSearchesResponse
	extends PaginatedResponse<RecentHotelSearch> {
	visibility: RecentHotelSearchVisibility
}

const emptyFilters = (): RecentHotelSearchFilters => ({
	mine: false,
	destination: '',
	from: '',
	to: '',
})

// La sección se desmonta en cada búsqueda y vuelve a montarse al terminar: los
// filtros viven fuera del componente para seguir puestos al volver.
const filters = ref<RecentHotelSearchFilters>(emptyFilters())

const activeFilterCount = computed(
	() =>
		[
			filters.value.mine,
			filters.value.destination.trim(),
			filters.value.from || filters.value.to,
		].filter(Boolean).length
)

const clearFilters = () => {
	filters.value = emptyFilters()
}

// Tarjetas de 3 en 3: el backend manda 6 (dos filas) por default y «Ver más» pide la
// página siguiente. No se manda `per_page`: el middleware de paginación de la API sólo
// acepta 5, 10, 15, 25, 50 o 100, y 6 respondería 422. Los filtros vacíos no viajan.
const fetchRecentHotelSearches = async (
	page: number,
	current: RecentHotelSearchFilters
): Promise<RecentHotelSearchesResponse> => {
	const destination = current.destination.trim()

	const { data } = await axios.get<RecentHotelSearchesResponse>(
		'/api/hotel-searches',
		{
			params: {
				page,
				...(current.mine && { mine: 1 }),
				...(destination && { destination }),
				...(current.from && { from: current.from }),
				...(current.to && { to: current.to }),
			},
		}
	)

	return data
}

/**
 * Historial de búsquedas de hotel que el usuario puede ver: las suyas, las de su gente
 * si es supervisor, o las de toda la empresa si es Master o Travel Manager. El backend
 * decide el alcance y lo regresa en `visibility`; aquí sólo se pagina y se filtra.
 */
const useRecentHotelSearches = () => {
	const {
		data,
		isLoading,
		isError,
		isFetching,
		isFetchingNextPage,
		hasNextPage,
		fetchNextPage,
	} = useInfiniteQuery(
		// Con los filtros en la llave, cambiar uno vuelve a pedir desde la página 1.
		['recentHotelSearches', filters],
		({ pageParam = 1 }) => fetchRecentHotelSearches(pageParam, filters.value),
		{
			// La sección se desmonta en cada búsqueda y vuelve a montarse al terminar.
			// Sin caché, al volver arranca de cero con el esqueleto en vez de enseñar la
			// lista vieja —sin la búsqueda que se acaba de hacer— mientras llega la nueva.
			cacheTime: 0,
			// Al cambiar un filtro las tarjetas anteriores se quedan, con el aviso de
			// «Actualizando…», en vez de brincar al esqueleto en cada tecla.
			keepPreviousData: true,
			getNextPageParam: (lastPage) => {
				const { currentPage, lastPage: total } = lastPage.meta

				return currentPage < total ? currentPage + 1 : undefined
			},
		}
	)

	const searches = computed<RecentHotelSearch[]>(() =>
		(data.value?.pages ?? []).flatMap((page) => page.data ?? [])
	)

	const visibility = computed<RecentHotelSearchVisibility | null>(
		() => data.value?.pages[0]?.visibility ?? null
	)

	return {
		searches,
		visibility,
		filters,
		activeFilterCount,
		clearFilters,
		isLoading,
		isError,
		isFetching,
		isFetchingNextPage,
		hasNextPage,
		fetchNextPage,
	}
}

export default useRecentHotelSearches
